import React, { useEffect, memo } from 'react';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from 'reactstrap';
import {
  useCreateMutation,
  useUpdateMutation,
  useDetailQuery,
  useUpdatePatchMutation,
} from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { MANUFACTURER_PATHS } from 'routes/routesConstants';
import { handleFormErrors } from 'utils/formErrorHandler';

interface ManufacturerFormValues {
  name: string;
  description?: string;
  logoUrl?: string;
  country?: string;
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Manufacturer name is required'),
  description: Yup.string().trim().max(500, 'Description must be at most 500 characters'),
  logoUrl: Yup.string()
    .trim()
    .url('Logo URL must be valid')
    .max(255, 'Logo URL must be at most 255 characters')
    .nullable(),
  country: Yup.string().trim().max(100, 'Country must be at most 100 characters'),
});

const CreateOrUpdateManufacturerForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const { data: fetchedManufacturer, isLoading: loadingManufacturer } =
    useDetailQuery<ManufacturerFormValues>(
      id ? `${API_ENDPOINTS.TENANT.MANUFACTURERS.WITH_ID.replace(':id', id)}` : '',
      { id },
    );

  const { mutate: createManufacturer, isPending: creating } = useCreateMutation<
    ManufacturerFormValues,
    ManufacturerFormValues
  >(API_ENDPOINTS.TENANT.MANUFACTURERS.BASE);
  const { mutate: updateManufacturer, isPending: updating } = useUpdatePatchMutation<
    ManufacturerFormValues,
    ManufacturerFormValues
  >(API_ENDPOINTS.TENANT.MANUFACTURERS.WITH_ID.replace(':id', id ?? ''));

  const formik = useFormik<ManufacturerFormValues>({
    initialValues: {
      name: fetchedManufacturer?.name ?? '',
      description: fetchedManufacturer?.description ?? '',
      logoUrl: fetchedManufacturer?.logoUrl ?? '',
      country: fetchedManufacturer?.country ?? '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, formikHelpers: FormikHelpers<ManufacturerFormValues>) => {
      const payload: ManufacturerFormValues = {
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        logoUrl: values.logoUrl?.trim() || undefined,
        country: values.country?.trim() || undefined,
      };

      const onSuccess = () => {
        navigate(MANUFACTURER_PATHS.BASE);
      };

      const onError = (error: any) => {
        handleFormErrors<ManufacturerFormValues>(error, formikHelpers);
      };

      if (isEditMode && id) {
        updateManufacturer({ id, data: payload }, { onSuccess, onError });
      } else {
        createManufacturer(payload, { onSuccess, onError });
      }
    },
  });

  if (isEditMode && loadingManufacturer) {
    return (
      <Container className="py-5 text-center">
        <Spinner color="primary" />
      </Container>
    );
  }

  return (
    <Row className="justify-content-center">
      <Col md={10} lg={8}>
        <Card className="p-4 shadow-sm">
          <CardBody>
            <h2 className="text-center fw-bold mb-4">
              {isEditMode ? 'Update Manufacturer' : 'Create Manufacturer'}
            </h2>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Row>
                <Col sm={12} md={12} lg={12}>
                  <FormGroup>
                    <Label for="name" className="fw-bold">
                      Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={Boolean(formik.touched.name && formik.errors.name)}
                      placeholder="Manufacturer name"
                      autoComplete="off"
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className="invalid-feedback d-block">{formik.errors.name}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col sm={12} md={12} lg={12}>
                  <FormGroup>
                    <Label for="country" className="fw-bold">
                      Country
                    </Label>
                    <Input
                      id="country"
                      name="country"
                      value={formik.values.country}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={Boolean(formik.touched.country && formik.errors.country)}
                      placeholder="Country"
                      autoComplete="off"
                    />
                    {formik.touched.country && formik.errors.country && (
                      <div className="invalid-feedback d-block">{formik.errors.country}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col sm={12} md={12} lg={12}>
                  <FormGroup>
                    <Label for="logoUrl" className="fw-bold">
                      Logo URL
                    </Label>
                    <Input
                      id="logoUrl"
                      name="logoUrl"
                      value={formik.values.logoUrl}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={Boolean(formik.touched.logoUrl && formik.errors.logoUrl)}
                      placeholder="Logo URL"
                      autoComplete="off"
                    />
                    {formik.touched.logoUrl && formik.errors.logoUrl && (
                      <div className="invalid-feedback d-block">{formik.errors.logoUrl}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col sm={12} md={12} lg={12}>
                  <FormGroup>
                    <Label for="description" className="fw-bold">
                      Description
                    </Label>
                    <Input
                      type="textarea"
                      id="description"
                      name="description"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={Boolean(formik.touched.description && formik.errors.description)}
                      placeholder="Description"
                      rows={4}
                    />
                    {formik.touched.description && formik.errors.description && (
                      <div className="invalid-feedback d-block">{formik.errors.description}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col sm={12} className="d-flex justify-content-end mt-3">
                  <Button color="primary" type="submit" disabled={creating || updating}>
                    {creating || updating
                      ? isEditMode
                        ? 'Updating...'
                        : 'Creating...'
                      : isEditMode
                      ? 'Update Manufacturer'
                      : 'Create Manufacturer'}
                  </Button>
                </Col>
              </Row>
            </form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default memo(CreateOrUpdateManufacturerForm);
